/* ═══════════════════════════════════════════════════════════════════════════
   GRAVV — Trail & Race Engine
   Pure functions: trail time prediction, pacing, nutrition, training plans.
   Sources: Naismith, Minetti, Banister PMC, Karvonen, Billat VMA
═══════════════════════════════════════════════════════════════════════════ */
import { T } from "../design/tokens";
import { secToHMS, paceFmt, parseLocalDate } from "./utils";

/* ── ROUTE CORRECTION ENGINE ────────────────────────────────────────────── */
export const COURSE_CORRECTIONS = {
  temperature: (t = 12) => {
    if (t <= 5)  return 1.030;
    if (t <= 10) return 1.008;
    if (t <= 15) return 1.000;
    if (t <= 20) return 1.012;
    if (t <= 25) return 1.030;
    if (t <= 30) return 1.055;
    return 1.080;
  },
  wind: (intensite = "nul", orientation = "variable") => {
    const base = { nul:0, leger:0.008, modere:0.020, fort:0.038, tres_fort:0.060 }[intensite] || 0;
    const mult = { face:1.0, lateral:0.55, dos:-0.45, variable:0.30 }[orientation] ?? 0.3;
    return 1 + base * mult;
  },
  denivele: (dplus = 0, dminus = 0, distKm = 10) => {
    if (!distKm) return 1;
    const dNetPer100m = (dplus - dminus * 0.5) / 100;
    const extraSecPerKm = dNetPer100m * 12;
    const basePaceEstimate = 300;
    return 1 + (extraSecPerKm / basePaceEstimate) * (dplus / (distKm * 10 + 1));
  },
  altitude: (alt = 0) => {
    if (alt < 500)  return 1.000;
    if (alt < 1000) return 1.010;
    if (alt < 1500) return 1.022;
    if (alt < 2000) return 1.038;
    return 1.058;
  },
  revetement: (r = "asphalte") => ({
    asphalte:1.000, beton:1.005, pave:1.025, gravier:1.040, terre:1.035,
  }[r] ?? 1.000),
  virages: (v = "larges") => ({ larges:1.000, mixtes:1.008, serres:1.018 }[v] ?? 1.000),
  humidite: (h = "normale") => ({ seche:1.000, normale:1.000, humide:1.008 }[h] ?? 1.000),
};

export const calcCourseMultiplier = (cp) => {
  if (!cp) return { total: 1, details: [] };
  const factors = [
    { label:"Température", value:COURSE_CORRECTIONS.temperature(cp.temperature), icon:"🌡️", raw:`${cp.temperature}°C` },
    { label:"Vent",        value:COURSE_CORRECTIONS.wind(cp.vent, cp.orientation_vent), icon:"💨", raw:`${cp.vent}·${cp.orientation_vent}` },
    { label:"Dénivelé",   value:COURSE_CORRECTIONS.denivele(cp.denivele_plus, cp.denivele_minus, cp.distance), icon:"⛰️", raw:`D+ ${cp.denivele_plus}m` },
    { label:"Altitude",   value:COURSE_CORRECTIONS.altitude(cp.altitude), icon:"🏔️", raw:`${cp.altitude}m` },
    { label:"Revêtement", value:COURSE_CORRECTIONS.revetement(cp.revetement), icon:"🛣️", raw:cp.revetement },
    { label:"Virages",    value:COURSE_CORRECTIONS.virages(cp.virages), icon:"↩️", raw:cp.virages },
    { label:"Humidité",   value:COURSE_CORRECTIONS.humidite(cp.humidite), icon:"💧", raw:cp.humidite },
  ];
  const total = factors.reduce((a, f) => a * f.value, 1);
  return { total: +total.toFixed(4), details: factors.map(f => ({ ...f, delta: +((f.value - 1) * 100).toFixed(2) })) };
};

/* ── STRATA TRAIL ENGINE V2 ────────────────────────────────────────────────
   Coefficients calibrés Naismith trail runners compétitifs.
   T1 (piste dure balisée) → T5 (granite/éboulis/hors sentier)
─────────────────────────────────────────────────────────────────────────── */
export const TRAIL_VMA_TABLE = {
  5: 0.93, 10: 0.87, 15: 0.83, 21: 0.80,
  30: 0.76, 42: 0.72, 60: 0.66, 80: 0.60, 100: 0.55, 160: 0.48,
};

export const trailVmaFrac = (dist) => {
  const keys = Object.keys(TRAIL_VMA_TABLE).map(Number).sort((a, b) => a - b);
  if (dist <= keys[0]) return TRAIL_VMA_TABLE[keys[0]];
  if (dist >= keys[keys.length - 1]) return TRAIL_VMA_TABLE[keys[keys.length - 1]];
  for (let i = 0; i < keys.length - 1; i++) {
    if (keys[i] <= dist && dist <= keys[i + 1]) {
      const t = (dist - keys[i]) / (keys[i + 1] - keys[i]);
      return TRAIL_VMA_TABLE[keys[i]] * (1 - t) + TRAIL_VMA_TABLE[keys[i + 1]] * t;
    }
  }
  return 0.77;
};

// D+ coefficients in s/m — T2 calibrated on Écotrail data
export const TRAIL_CLIMB_COEFF   = { 1: 1.5, 2: 2.0, 3: 2.8, 4: 4.2, 5: 5.5 };
// D- coefficients — negative = time gain, positive = braking cost
export const TRAIL_DESC_COEFF    = { 1: -0.4, 2: -0.2, 3: 0.6, 4: 1.3, 5: 2.2 };
// Technicity overhead — T2 = standard trail = 1.00
export const TRAIL_TECH_OVERHEAD = { 1: 0.98, 2: 1.00, 3: 1.04, 4: 1.10, 5: 1.18 };

// Environmental correction factors (additive on total)
export const TRAIL_ENV = {
  chaleur: (temp = 18, humidite = "normale") => {
    const humBonus = { seche: -0.02, normale: 0.00, humide: 0.03, tres_humide: 0.06 }[humidite] || 0;
    const tempDelta =
      temp <= 14 ? 0.00 :
      temp <= 18 ? 0.01 :
      temp <= 22 ? 0.03 :
      temp <= 26 ? 0.055 :
      temp <= 30 ? 0.082 :
      temp <= 35 ? 0.110 : 0.130;
    return Math.max(0, tempDelta + humBonus);
  },
  altitude: (altDepart = 0, altMax = 0) => {
    // 60% altMax + 40% altDepart — more time spent climbing
    const altEff = altDepart * 0.4 + altMax * 0.6;
    if (altEff < 800)  return 0.000;
    if (altEff < 1200) return 0.010;
    if (altEff < 1500) return 0.022;
    // Above 1500m: +2.8% per 400m bracket (VO2max −1%/300m)
    const brackets = Math.ceil((altEff - 1500) / 400);
    return Math.min(0.022 + brackets * 0.028, 0.120);
  },
  exposition: (e = "partielle") => ({
    ombragee: 0.000, partielle: 0.015, exposee: 0.040, tres_exposee: 0.065,
  }[e] || 0.015),
  hydrologie: (h = "sec") => ({
    sec: 0.000, quelques_passages: 0.010, passages_frequents: 0.025, gues_majeurs: 0.050,
  }[h] || 0.000),
  vent: (intensite = "nul", orientation = "variable") => {
    const base = { nul: 0, leger: 0.004, modere: 0.010, fort: 0.020, tres_fort: 0.035 }[intensite] || 0;
    const mult = { face: 1.0, lateral: 0.5, dos: -0.3, variable: 0.25 }[orientation] ?? 0.25;
    return Math.max(0, base * mult);
  },
};

export const calcTrailTime = (cp, vma) => {
  if (!cp || !vma) return null;
  const dist   = cp.distance || 20;
  const tech   = Math.round(Math.max(1, Math.min(5, cp.technicite || 2)));
  const dplus  = cp.denivele_plus  || 0;
  const dminus = cp.denivele_minus || 0;

  // EKM affiché (effort perçu) — D-/300 car descente moins pénalisante
  const ekm = +(dist + dplus / 100 + dminus / 300).toFixed(1);

  const frac = trailVmaFrac(dist);
  const flatPaceSec = (60 / (vma * frac)) * 60; // s/km

  const flatTime   = flatPaceSec * dist * TRAIL_TECH_OVERHEAD[tech];
  const dplusTime  = dplus  * TRAIL_CLIMB_COEFF[tech];
  const dminusTime = dminus * TRAIL_DESC_COEFF[tech];
  const baseTime   = flatTime + dplusTime + dminusTime;

  const envDeltas = [
    { key:"chaleur",    label:"Chaleur + humidité", icon:"🌡️", delta: TRAIL_ENV.chaleur(cp.temperature, cp.humidite) },
    { key:"altitude",   label:"Altitude",            icon:"🏔️", delta: TRAIL_ENV.altitude(cp.altitude_depart, cp.altitude_max) },
    { key:"exposition", label:"Exposition solaire",  icon:"☀️", delta: TRAIL_ENV.exposition(cp.exposition) },
    { key:"hydrologie", label:"Hydrologie",          icon:"🌊", delta: TRAIL_ENV.hydrologie(cp.hydrologie) },
    { key:"vent",       label:"Vent",                icon:"💨", delta: TRAIL_ENV.vent(cp.vent, cp.orientation_vent) },
  ];
  const envFactor    = 1 + envDeltas.reduce((s, e) => s + e.delta, 0);
  const estimatedSec = Math.round(baseTime * envFactor);

  return {
    estimatedSec,
    estimatedTime: secToHMS(estimatedSec),
    flatPaceSec,
    flatPaceStr:   paceFmt(flatPaceSec),
    flatTime,
    flatTimeStr:   secToHMS(Math.round(flatTime)),
    dplusTime,
    dplusTimeStr:  secToHMS(Math.round(dplusTime)),
    dminusTime,
    dminusTimeStr: secToHMS(Math.round(Math.abs(dminusTime))),
    naismith:      baseTime,
    naiSmithTime:  secToHMS(Math.round(baseTime)),
    envFactor,
    envDeltas,
    ekm,
    tech,
    climbCoeff:  TRAIL_CLIMB_COEFF[tech],
    descCoeff:   TRAIL_DESC_COEFF[tech],
    pctFlat:     Math.round(flatTime   / Math.max(baseTime, 1) * 100),
    pctDplus:    Math.round(dplusTime  / Math.max(baseTime, 1) * 100),
    pctDminus:   Math.round(Math.abs(dminusTime) / Math.max(baseTime, 1) * 100),
  };
};

export const trailTechPenalty = (tech) => {
  const t = Math.round(Math.max(1, Math.min(5, tech)));
  return Math.round((TRAIL_TECH_OVERHEAD[t] - 1) * 100);
};

/* ── Format helpers ─────────────────────────────────────────────────────── */
export const fmtDelta = (delta) => {
  if (Math.abs(delta) < 0.05) return { text: "Neutre", color: T.textLight };
  return delta > 0
    ? { text: `+${delta.toFixed(1)}%`, color: T.red }
    : { text: `${delta.toFixed(1)}%`,  color: T.green };
};

export const applyMultiplierToTime = (baseSec, mult) => {
  const adjSec  = Math.round(baseSec * mult);
  const diffSec = adjSec - baseSec;
  const sign    = diffSec >= 0 ? "+" : "-";
  const abs     = Math.abs(diffSec);
  const m       = Math.floor(abs / 60), s = abs % 60;
  return {
    adjSec,
    adjTime:  secToHMS(adjSec),
    diffText: `${sign}${m > 0 ? m + "min " : ""}${s}s`,
    diffColor: diffSec > 0 ? T.red : T.green,
  };
};

/* ── Pacing & cardiac models ────────────────────────────────────────────── */
export const getPaceMultipliers = (n, strategy) =>
  Array.from({ length: n }, (_, i) => {
    const pos = i / (n - 1 || 1);
    if (strategy === "even")     return 1;
    if (strategy === "negative") return pos < 0.5 ? 1.025 - pos * 0.05 : 0.975 + (pos - 0.5) * (-0.05);
    if (strategy === "positive") return 0.97 + pos * 0.1;
    return 1;
  });

export const intensityRange = (distKm, isTrail = false) => {
  if (isTrail) {
    return distKm < 20 ? { start: 0.75, end: 0.87 } : { start: 0.70, end: 0.82 };
  }
  if (distKm <= 5)    return { start: 0.89, end: 0.97 };
  if (distKm <= 10)   return { start: 0.83, end: 0.93 };
  if (distKm <= 21.5) return { start: 0.78, end: 0.88 };
  return                     { start: 0.72, end: 0.83 };
};

export const calcRaceFC = (pos, fcMax, fcRepos, distKm, isTrail = false) => {
  const { start, end } = intensityRange(distKm, isTrail);
  const curved = start + (end - start) * Math.pow(pos, isTrail ? 0.65 : 0.8);
  return Math.round(fcRepos + (fcMax - fcRepos) * curved);
};

export const getZoneFromFC = (fc, fcMax, fcRepos) => {
  const hrr = (fc - fcRepos) / (fcMax - fcRepos);
  if (hrr < 0.60) return { z: "Z1", c: T.blue,   label: "Récup" };
  if (hrr < 0.70) return { z: "Z2", c: T.green,  label: "Fond" };
  if (hrr < 0.80) return { z: "Z3", c: "#CA8A04", label: "Tempo" };
  if (hrr < 0.90) return { z: "Z4", c: T.orange, label: "Seuil" };
  return                 { z: "Z5", c: T.red,    label: "VO2max" };
};

/* ── Road splits ────────────────────────────────────────────────────────── */
export const buildSplits = (distKm, targetSec, strategy, fcMax, fcRepos) => {
  const step = distKm <= 10 ? 1 : 5;
  const cps  = [];
  for (let k = step; k < distKm; k += step) cps.push(+k.toFixed(1));
  cps.push(+distKm.toFixed(1));
  const multipliers = getPaceMultipliers(cps.length, strategy);
  const basePace    = targetSec / distKm;
  let elapsed = 0;
  return cps.map((km, i) => {
    const segLen  = i === 0 ? km : km - cps[i - 1];
    const adjPace = basePace * multipliers[i];
    elapsed += adjPace * segLen;
    const fc   = calcRaceFC(km / distKm, fcMax, fcRepos, distKm);
    return {
      km, segLen,
      pace: paceFmt(adjPace), paceNum: adjPace,
      elapsed: secToHMS(elapsed), elapsedSec: elapsed,
      fc, zone: getZoneFromFC(fc, fcMax, fcRepos), pos: km / distKm,
    };
  });
};

/* ── Trail splits — per D+ segment ─────────────────────────────────────── */
export const buildTrailSplits = (segments, fcMax, fcRepos, vma) => {
  const totalDist = segments.reduce((s, x) => s + x.distKm, 0) || 20;
  const frac      = trailVmaFrac(totalDist);
  const basePaceSec = vma && vma > 0
    ? (60 / (vma * frac)) * 60
    : 380; // fallback 6:20/km

  let elapsed = 0;
  return segments.map((seg, i) => {
    const dist    = Math.max(seg.distKm, 0.1);
    const tech    = 2;
    const dplus   = seg.dplus  || 0;
    const dminus  = seg.dminus || 0;

    const flatTime   = basePaceSec * dist;
    const dplusTime  = dplus  * TRAIL_CLIMB_COEFF[tech];
    const dminusTime = dminus * TRAIL_DESC_COEFF[tech];
    const segTime    = flatTime + dplusTime + dminusTime;
    const effectivePace = segTime / dist;

    elapsed += segTime;

    const pos  = (i + 1) / segments.length;
    const fc   = calcRaceFC(pos, fcMax, fcRepos, totalDist, true);
    const zone = getZoneFromFC(fc, fcMax, fcRepos);

    return {
      ...seg,
      paceNum:    effectivePace,
      pace:       paceFmt(effectivePace),
      paceLabel:  paceFmt(effectivePace) + "/km",
      elapsed:    secToHMS(elapsed),
      elapsedSec: elapsed,
      segTimeSec: segTime,
      fc, zone,
    };
  });
};

/* ── Trail nutrition ────────────────────────────────────────────────────── */
export const buildTrailNutrition = (distKm, targetSec, nbRavitos = 3, tempChaud = false) => {
  const plan   = [];
  const durMin = targetSec / 60;

  plan.push({ km:0, time:"J-2",   type:"prep",  icon:"🍝", label:"Charge glucidique J-2 et J-1",
    detail:"2 jours de charge : riz, pâtes, quinoa à chaque repas. Augmentez les portions de 30%. Évitez légumes crus, choux, legumineuses → flatulences pendant l'effort.", color:T.textMid });
  plan.push({ km:0, time:"-3h",   type:"repas", icon:"🍌", label:"Petit-déjeuner trail",
    detail:"Pain blanc + confiture + banane + flocons d'avoine. Café si habitué. Complément de sel (capsule ou pincée). 500ml eau au réveil.", color:T.textMid });
  plan.push({ km:0, time:"-20min",type:"hydra", icon:"💧", label:"Pré-hydratation",
    detail:"200ml eau avec électrolytes. Vérifier couleur urine : jaune pâle = bien hydraté.", color:T.blue });

  const trailFoods = [
    { type:"gel",   icon:"⚡", label:"Gel énergétique",       detail:"Gel isotonique 25g glucides. Avec 150ml eau minimum. Alterner glucose/fructose si > 3h d'effort.", color:T.orange },
    { type:"barre", icon:"🍫", label:"Barre énergétique",     detail:"Barre 40-50g glucides. Mâchez bien. Idéal sur segments plats ou descentes où vous pouvez manger sans risque.", color:"#92400e" },
    { type:"fruit", icon:"🍊", label:"Ravitaillement solide", detail:"Orange, banane, dattes. L'estomac supporte mieux le solide sur de longues durées. Riche en fructose + minéraux.", color:"#b45309" },
    { type:"hydra", icon:"💧", label:"Eau + électrolytes",    detail:"Au minimum 500ml/heure par temps chaud. Capsules de sel toutes les 45-60 min si >25°C ou sueur visible sur les tempes.", color:T.blue },
    { type:"sel",   icon:"🧂", label:"Capsule de sel",        detail:"Sodium : 500-800mg/h en trail chaud. Symptôme d'hyponatrémie (manque de sel) : crampes, nausées, confusion. Ne pas attendre ces signes.", color:"#6b7280" },
  ];

  const schedule = [
    { pos:0.08, food:trailFoods[4] },
    { pos:0.15, food:trailFoods[0] },
    { pos:0.22, food:trailFoods[3] },
    { pos:0.30, food:trailFoods[1] },
    { pos:0.38, food:trailFoods[4] },
    { pos:0.45, food:trailFoods[2] },
    { pos:0.52, food:trailFoods[0] },
    { pos:0.58, food:trailFoods[3] },
    { pos:0.64, food:trailFoods[4] },
    { pos:0.70, food:trailFoods[1] },
    { pos:0.77, food:trailFoods[0] },
    { pos:0.83, food:trailFoods[2] },
    { pos:0.89, food:trailFoods[4] },
    { pos:0.94, food:trailFoods[0] },
  ];

  schedule.forEach(e => {
    const km = +(distKm * e.pos).toFixed(1);
    plan.push({ km, time: secToHMS(targetSec * e.pos), ...e.food });
  });

  if (tempChaud) {
    plan.push({ km: distKm * 0.33, time: secToHMS(targetSec * 0.33), type:"chaleur", icon:"🌡️",
      label:"Protocole chaleur — rafraîchissement",
      detail:"Versez de l'eau froide sur la nuque, les poignets et le haut de la tête aux ravitaillements. Réduisez l'allure de 10-15% si FC > 90% FCmax par chaleur.", color:T.red });
  }

  plan.push({ km: distKm, time: secToHMS(targetSec), type:"arrivee", icon:"🏅", label:"Arrivée — Récupération trail",
    detail:"1. Continuez à marcher 10 min. 2. Boisson de récup dans les 20 min (glucides+protéines 4:1). 3. Sel et électrolytes si jambes qui crampes. 4. Évitez l'alcool 3h après. 5. Bain froid des jambes si possible.", color:T.green });

  return plan.sort((a, b) => a.km - b.km);
};

/* ── Road nutrition ─────────────────────────────────────────────────────── */
export const buildNutritionPlan = (distKm, targetSec) => {
  const plan   = [];
  const durMin = targetSec / 60;
  plan.push({ km:0, time:"J-1 soir", type:"repas", icon:"🍝", label:"Dîner de charge",      detail:"Pâtes, riz ou quinoa. 70% glucides, peu de fibres. Pas de nouveauté.",  color:T.textMid });
  plan.push({ km:0, time:"-3h",      type:"repas", icon:"🍌", label:"Petit-déjeuner de course", detail:"Flocons + banane + café léger. Évitez fibres, lait entier.",           color:T.textMid });
  plan.push({ km:0, time:"-30min",   type:"hydra", icon:"💧", label:"Pré-hydratation",       detail:"300–500 ml d'eau.",                                                     color:T.blue });

  if (durMin < 45) {
    plan.push({ km:distKm*0.5, time:secToHMS(targetSec*0.5), type:"hydra", icon:"💧", label:"Eau", detail:"Petite gorgée si disponible.", color:T.blue });
  } else if (durMin < 75) {
    plan.push({ km:distKm*0.35, time:secToHMS(targetSec*0.35), type:"hydra", icon:"💧", label:"Eau",          detail:"100–150 ml.",                 color:T.blue });
    plan.push({ km:distKm*0.55, time:secToHMS(targetSec*0.55), type:"gel",   icon:"⚡", label:"Gel #1 + eau", detail:"25g glucides avec 150 ml d'eau.", color:T.orange });
    plan.push({ km:distKm*0.8,  time:secToHMS(targetSec*0.8),  type:"hydra", icon:"💧", label:"Eau",          detail:"Dernière prise.",                color:T.blue });
  } else if (durMin < 130) {
    [[0.20,"hydra","💧","Eau","100–150ml."],
     [0.35,"gel","⚡","Gel #1 + eau","25g + 150ml eau."],
     [0.55,"hydra","💧","Eau + élec.","Isotonique ou eau + sel."],
     [0.70,"gel","⚡","Gel #2","Caféiné possible."],
     [0.85,"hydra","💧","Eau","Dernière."]].forEach(([p, t, ic, l, d]) =>
      plan.push({ km:+(distKm*p).toFixed(1), time:secToHMS(targetSec*p), type:t, icon:ic, label:l, detail:d, color:t==="gel"?T.orange:T.blue }));
  } else {
    [[0.10,"hydra","💧","Eau","Petites gorgées."],
     [0.22,"gel","⚡","Gel #1","~30min, évite le mur."],
     [0.33,"hydra","💧","Eau + élec.","Électrolytes anti-crampes."],
     [0.45,"gel","⚡","Gel #2","60g/h max."],
     [0.57,"hydra","💧","Eau","Déshydratation = -20% perf."],
     [0.68,"gel","⚡","Gel #3 caféiné","75–100mg caféine."],
     [0.78,"hydra","💧","Eau + élec.","10 derniers km."],
     [0.88,"gel","⚡","Gel #4","Si supporté."],
     [0.95,"hydra","💧","Eau","Dernier ravito."]].forEach(([p, t, ic, l, d]) =>
      plan.push({ km:+(distKm*p).toFixed(1), time:secToHMS(targetSec*p), type:t, icon:ic, label:l, detail:d, color:t==="gel"?T.orange:T.blue }));
  }
  plan.push({ km:distKm, time:secToHMS(targetSec), type:"arrivee", icon:"🏅", label:"Arrivée", detail:"Dans les 30 min : récup glucides+protéines, banane, eau.", color:T.green });
  return plan.sort((a, b) => a.km - b.km);
};

export const DEFAULT_TRAIL_SEGMENTS = [
  { label:"Départ → Bergeries",  distKm:6, dplus:450,  dminus:0,    type:"montee"   },
  { label:"Bergeries → Col",     distKm:5, dplus:600,  dminus:0,    type:"montee"   },
  { label:"Col → Lac",           distKm:4, dplus:200,  dminus:150,  type:"montee"   },
  { label:"Lac → Sommet",        distKm:3, dplus:350,  dminus:0,    type:"montee"   },
  { label:"Retour descente",     distKm:9, dplus:50,   dminus:1300, type:"descente" },
];

/* ═══════════════════════════════════════════════════════════════════════════
   TRAINING PLAN GENERATOR v2
   Spec: Bompa periodization, Billat VMA zones, Banister CTL/ATL, ACWR < 1.4
═══════════════════════════════════════════════════════════════════════════ */
export const PLAN_TEMPLATES = {
  "5k":          { peakKm: 42,  taperWeeks: 1, sessions: 3, isTrail: false },
  "10k":         { peakKm: 50,  taperWeeks: 2, sessions: 4, isTrail: false },
  "semi":        { peakKm: 60,  taperWeeks: 2, sessions: 4, isTrail: false },
  "marathon":    { peakKm: 75,  taperWeeks: 3, sessions: 5, isTrail: false },
  "trail_court": { peakKm: 60,  taperWeeks: 2, sessions: 4, isTrail: true,  peakDplus: 1500 },
  "trail_long":  { peakKm: 85,  taperWeeks: 3, sessions: 5, isTrail: true,  peakDplus: 2500 },
  "ultra":       { peakKm: 100, taperWeeks: 4, sessions: 5, isTrail: true,  peakDplus: 3000 },
  "forme":       { peakKm: 40,  taperWeeks: 1, sessions: 3, isTrail: false },
};

// Taper volumes as fraction of peakKm, backward from race: [J-3w, J-2w, J-1w, raceWeek]
export const TAPER_DEPTHS = {
  "5k":          [null,  0.65, 0.45, 0.25],
  "10k":         [null,  0.65, 0.45, 0.25],
  "semi":        [null,  0.60, 0.40, 0.22],
  "marathon":    [0.70,  0.55, 0.35, 0.22],
  "trail_court": [null,  0.60, 0.40, 0.22],
  "trail_long":  [0.65,  0.50, 0.35, 0.22],
  "ultra":       [0.70,  0.55, 0.40, 0.25, 0.20],
  "forme":       [null,  0.65, 0.45, 0.25],
};

export const PHASE_NAMES  = ["Base", "Build", "Spécifique", "Taper"];
export const PHASE_COLORS = [T.blue, T.orange, T.gold, T.green];
export const PHASE_BG     = [T.blueLight, T.orangeLight, T.goldLight, T.greenLight];

export function raceTypeFromDistance(dist, isTrail = false) {
  const d = +dist;
  if (!d) return "trail_court";
  if (!isTrail) {
    if (d <= 6)  return "5k";
    if (d <= 12) return "10k";
    if (d <= 23) return "semi";
    if (d <= 45) return "marathon";
    return "trail_court"; // fallback
  }
  // Trail
  if (d <= 35) return "trail_court";
  if (d <= 80) return "trail_long";
  return "ultra";
}

// Multiplicateurs volume selon niveau (appliqués à peakKm)
const LEVEL_MULTIPLIERS = {
  debutant:  0.65,
  "1an":     0.80,
  "3ans":    1.00,
  expert:    1.15,
};

// Sessions structure par type
const SESSION_STRUCTURE = {
  "récup":         { ressenti: 1, icon: "💤", couleur: "blue"   },
  "fondamentale":  { ressenti: 2, icon: "🏃", couleur: "green"  },
  "sortie_longue": { ressenti: 3, icon: "🗻", couleur: "navy"   },
  "vma":           { ressenti: 5, icon: "🔥", couleur: "red"    },
  "seuil":         { ressenti: 4, icon: "⚡", couleur: "orange" },
  "côtes":         { ressenti: 4, icon: "⛰️", couleur: "textMid"},
};

// Alternatives urbaines pour les séances de côtes trail
const URBAN_HILL_ALTERNATIVES = [
  "Escaliers immeuble/stade : 10×montée 3-4 étages effort Z4, descente trot",
  "Tapis incliné 12-15% : 8×3min à effort seuil, récup 2min plat",
  "Parking rampe : 6×montée spirale P4 niveaux, descente trot",
];

export function generateTrainingPlan(profile, runs, upcoming) {
  if (!upcoming || !upcoming.length) return null;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const lastRace = upcoming[upcoming.length - 1];
  const [ly, lm, ld] = lastRace.date.split("-").map(Number);
  const lastRaceDate = new Date(ly, lm - 1, ld);
  const totalWeeks   = Math.round((lastRaceDate - today) / (7 * 24 * 3600 * 1000));
  if (totalWeeks < 2) return null;

  const hardest     = [...upcoming].sort((a, b) => b.distance - a.distance)[0];
  const dominantObj = raceTypeFromDistance(hardest.distance, !!hardest.isTrail);
  const tpl         = PLAN_TEMPLATES[dominantObj] || PLAN_TEMPLATES["trail_court"];
  const taperDepths = TAPER_DEPTHS[dominantObj]   || TAPER_DEPTHS["trail_court"];
  const isTrailPlan = !!tpl.isTrail;

  const vma     = profile.vma      || 14;
  const fcMax   = profile.fc_max   || 190;
  const fcRepos = profile.fc_repos || 55;
  const fcR     = fcMax - fcRepos;

  const fc = (pct) => Math.round(fcRepos + fcR * pct);
  const p  = (pct) => {
    const sec = 3600 / (vma * pct);
    return `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, "0")} /km`;
  };

  // Starting volume: real recent km/week, not CTL (TRIMP ≠ km)
  const fourWeeksAgo   = new Date(today); fourWeeksAgo.setDate(today.getDate() - 28);
  const recentRuns     = (runs || []).filter(r => r.distance > 0 && parseLocalDate(r.date) >= fourWeeksAgo);
  const recentWeeklyKm = recentRuns.reduce((s, r) => s + r.distance, 0) / 4;

  // Niveau : multiplier le peakKm cible
  const levelMult = LEVEL_MULTIPLIERS[profile.experienceCourse] || 1.0;
  // Cap based on realistic 8%/week compounding over available build weeks (70% of plan).
  // Avoids capping a 24-week ultra plan at 1.5× when 3× is physiologically achievable.
  const buildWeeks       = Math.max(4, Math.floor(totalWeeks * 0.70));
  const maxBuildMult     = Math.min(3.5, Math.pow(1.08, buildWeeks));
  const adjustedPeakKm   = Math.min(
    Math.round(tpl.peakKm * levelMult),
    Math.round((recentWeeklyKm || 20) * maxBuildMult)
  );
  const effectivePeakKm = adjustedPeakKm;

  // Sessions par semaine depuis le profil
  const userSessionsPerWeek = Math.max(2, Math.min(profile.seancesParSemaine || tpl.sessions, 7));
  const effectiveSessions   = Math.min(userSessionsPerWeek, tpl.sessions + 1);

  // Urban runner : pas de vraies côtes disponibles
  const isUrban = !!profile.terrainAccess && profile.terrainAccess === "urban_flat";

  // Start from actual recent volume; never start below 40% of peak to avoid too-flat early weeks
  const startKm = Math.max(
    Math.round(effectivePeakKm * 0.40),
    Math.min(Math.round(recentWeeklyKm || 20), Math.round(effectivePeakKm * 0.65))
  );

  // Map weekNum → race
  const raceMilestones = {};
  upcoming.forEach(r => {
    const [y, m, d] = r.date.split("-").map(Number);
    const wNum = Math.round((new Date(y, m - 1, d) - today) / (7 * 24 * 3600 * 1000));
    if (wNum >= 1 && wNum <= totalWeeks) raceMilestones[wNum] = r;
  });

  // Taper weeks anchored backward from each race
  const taperWeekMap = {};
  Object.entries(raceMilestones).forEach(([wStr, race]) => {
    const rWNum  = +wStr;
    const rType  = raceTypeFromDistance(race.distance, !!race.isTrail);
    const tw     = (PLAN_TEMPLATES[rType] || tpl).taperWeeks;
    const depths = TAPER_DEPTHS[rType] || taperDepths;
    for (let back = 1; back <= tw; back++) {
      const wn = rWNum - back;
      if (wn >= 1 && !raceMilestones[wn]) {
        const depthIdx = depths.length - 1 - back;
        taperWeekMap[wn] = { depth: depths[depthIdx] || 0.45, raceWNum: rWNum };
      }
    }
  });

  const weeks = [];
  let prevBuildVol = startKm;

  for (let weekNum = 1; weekNum <= totalWeeks; weekNum++) {
    const isRaceWeek = !!raceMilestones[weekNum];
    const taperInfo  = taperWeekMap[weekNum];
    const isTaper    = !isRaceWeek && !!taperInfo;
    const isRecovery = !isRaceWeek && !isTaper && weekNum > 1 && (weekNum % 4 === 0);
    const progress   = totalWeeks > 1 ? (weekNum - 1) / (totalWeeks - 1) : 0;

    const ph         = isRaceWeek ? 3 : isTaper ? 3 : progress < 0.30 ? 0 : progress < 0.65 ? 1 : 2;
    const isBase     = ph === 0;
    const isBuild    = ph === 1;
    const isSpecific = ph === 2;

    // Volume — ACWR-safe, +10%/week ramp cap
    let volumeTarget;
    if (isRaceWeek) {
      volumeTarget = Math.round(effectivePeakKm * (taperDepths[taperDepths.length - 1] || 0.22));
    } else if (isTaper) {
      volumeTarget = Math.round(effectivePeakKm * taperInfo.depth);
    } else if (isRecovery) {
      volumeTarget = Math.round(prevBuildVol * 0.72);
    } else {
      const linearTarget = startKm + (effectivePeakKm - startKm) * Math.min(progress * 1.4, 1);
      volumeTarget = Math.min(Math.round(linearTarget), Math.round(prevBuildVol * 1.10));
      prevBuildVol = volumeTarget;
    }

    // D+ for trail plans
    let dplus = 0;
    if (isTrailPlan && !isRaceWeek) {
      if (isSpecific) {
        const specWeeks = Math.round(totalWeeks * 0.20);
        const specIdx   = weekNum - Math.round(totalWeeks * 0.65);
        dplus = Math.round(1300 + Math.max(0, (specIdx - 1) / Math.max(1, specWeeks - 1)) * 900);
      } else if (isBuild) {
        dplus = Math.round(tpl.peakDplus * 0.25);
      } else if (isTaper) {
        dplus = Math.round(tpl.peakDplus * (taperInfo.raceWNum - weekNum <= 1 ? 0.10 : 0.25));
      } else if (isBase) {
        dplus = Math.round(tpl.peakDplus * 0.20);
      }
    }

    const isB2B = isTrailPlan && isSpecific && !isRecovery && !isTaper;
    const specWeekIdx = isSpecific ? weekNum - Math.round(totalWeeks * 0.65) : -1;
    const hasNightSession = isTrailPlan && (specWeekIdx === 2 || specWeekIdx === 3);
    const nbS = isRaceWeek ? 2 : (isTaper || isRecovery) ? 3 : isB2B ? 5 : effectiveSessions;

    const FRACS = {
      2: [0.55, 0.45],
      3: [0.42, 0.35, 0.23],
      4: [0.37, 0.26, 0.20, 0.17],
      5: [0.34, 0.22, 0.18, 0.15, 0.11],
    };
    const fracs  = FRACS[Math.min(nbS, 5)] || FRACS[3];
    const rawKms = fracs.map(f => Math.max(1, Math.round(volumeTarget * f)));
    rawKms[0] += volumeTarget - rawKms.reduce((a, b) => a + b, 0);

    const vmaSessionDesc = (() => {
      const buildStart = Math.round(totalWeeks * 0.30);
      const buildIdx   = weekNum - buildStart;
      if (isBuild) {
        if (buildIdx <= 1) return `Échauffement 15min → 8×400m à ${p(1.00)} — récup 1'30" trot → retour 10min`;
        if (buildIdx <= 2) return `Échauffement 15min → 10×400m à ${p(1.00)} — récup 1'30" → retour 10min`;
        if (buildIdx <= 3) return `Échauffement 15min → 6×600m à ${p(0.97)} — récup 2'00" → retour 10min`;
        return `Échauffement 15min → 5×1000m à ${p(0.95)} — récup 2'30" → retour 10min`;
      }
      if (isSpecific && !isTrailPlan) return `6×400m à ${p(1.00)} — récup 1'30" — séance courte pré-affûtage`;
      return `6×400m à ${p(1.00)} — récup 1'30"`;
    })();

    const sessions = [];

    // Helper: build côtes description (urban alternative if needed)
    const cotesDesc = (nbReps, dplusFrac) => isUrban
      ? `🏙️ Alternative urbaine — ${URBAN_HILL_ALTERNATIVES[0]}`
      : `Côtes — ${nbReps}×200m montée dynamique + descente contrôlée — D+${Math.round(dplus * dplusFrac)}m`;
    const cotesStructureBloc = (nbReps, dplusFrac) => isUrban
      ? { label: "Alternative urbaine", detail: URBAN_HILL_ALTERNATIVES[0] }
      : { label: `${nbReps}×200m côtes`, detail: `Montée dynamique Z4 — FC ${fc(0.88)}–${fc(0.94)} bpm — descente trot — D+${Math.round(dplus * dplusFrac)}m` };

    if (isRaceWeek) {
      const race    = raceMilestones[weekNum];
      const isUltra = race.isTrail && race.distance >= 60;
      const raceDay = isUltra ? "Samedi" : "Dimanche";
      sessions.push({ day:"Mercredi", type:"récup", km:rawKms[1],
        description:`Footing léger pré-course — Z1 — FC < ${fc(0.60)} bpm — jambes légères`,
        structure:[{ label:"Footing Z1", detail:`${rawKms[1]} km à ${p(0.62)} /km — FC < ${fc(0.60)} bpm` }],
        fcTarget: fc(0.60), ressenti: 1 });
      if (!isUltra) {
        sessions.push({ day:"Vendredi", type:"récup", km:Math.min(5, rawKms[0]),
          description:`20min très léger + 4×80m accélérations — pas de fatigue`,
          structure:[{ label:"Activation", detail:`20 min Z1 + 4×80m accélérations progressives` }],
          fcTarget: fc(0.58), ressenti: 1 });
      }
      sessions.push({ day:raceDay, type:"sortie_longue", km:race.distance,
        description:`🏁 ${race.nom} — ${race.distance} km${race.objectif ? ` — Objectif ${race.objectif}` : ""}`,
        structure:[{ label:"Course", detail:`${race.distance} km — ${race.objectif || "Effort contrôlé"}` }],
        fcTarget: fc(0.85), ressenti: 4, isRaceDay:true });
    } else if (isB2B) {
      sessions.push({ day:"Lundi", type:"récup", km:rawKms[4],
        description:`Z1 — ${p(0.62)} — FC < ${fc(0.55)} bpm`,
        structure:[{ label:"Footing Z1", detail:`${rawKms[4]} km à ${p(0.62)} /km — FC < ${fc(0.55)} bpm` }],
        fcTarget: fc(0.55), ressenti: 1 });
      sessions.push({ day:"Mardi", type:"fondamentale", km:rawKms[2],
        description:`Z2 trail technique — ${p(0.67)} — D+${Math.round(dplus*0.10)}m`,
        structure:[
          { label:"Échauffement", detail:`10 min à ${p(0.62)} /km` },
          { label:"Corps fondamental", detail:`${rawKms[2]} km à ${p(0.67)} /km — FC ${fc(0.64)}–${fc(0.72)} bpm — D+${Math.round(dplus*0.10)}m` },
          { label:"Retour calme", detail:`5 min très lent` },
        ],
        fcTarget: fc(0.68), ressenti: 2 });
      if (hasNightSession && specWeekIdx === 2) {
        sessions.push({ day:"Mercredi", type:"côtes", km:rawKms[3],
          description:`🌙 Sortie nocturne — départ 23h — 2h Z2 lampe frontale — tester nutrition solide`,
          structure:[
            { label:"Échauffement 10min plat", detail:`à ${p(0.65)} /km` },
            { label:"Nocturne Z2", detail:`${rawKms[3]} km à ${p(0.65)} /km — FC < ${fc(0.70)} bpm — lampe frontale` },
            { label:"Retour calme", detail:`Marche 10 min` },
          ],
          fcTarget: fc(0.65), ressenti: 3 });
      } else {
        const nbReps = specWeekIdx <= 1 ? 8 : specWeekIdx <= 2 ? 10 : 12;
        sessions.push({ day:"Mercredi", type:"côtes", km:rawKms[3],
          description: cotesDesc(nbReps, 0.18),
          structure:[
            { label:"Échauffement 10min plat", detail:`à ${p(0.65)} /km — FC < ${fc(0.65)} bpm` },
            cotesStructureBloc(nbReps, 0.18),
            { label:"Retour calme plat", detail:`10 min à ${p(0.62)} /km` },
          ],
          fcTarget: fc(0.88), ressenti: 4 });
      }
      const satDesc = hasNightSession && specWeekIdx === 3
        ? `🌄 Simulation ultra — départ 05h — ${rawKms[0]}km trail + D+${Math.round(dplus*0.45)}m — ravitos 45min — allure ${p(0.65)}`
        : `Sortie longue trail ${rawKms[0]}km — Z2 intégral — D+${Math.round(dplus*0.45)}m — nutrition 45min — FC ${fc(0.60)}–${fc(0.72)} bpm`;
      sessions.push({ day:"Samedi", type:"sortie_longue", km:rawKms[0],
        description: satDesc,
        structure:[
          { label:"Départ lent", detail:`20 min à ${p(0.62)} /km` },
          { label:"Corps Z2", detail:`${rawKms[0]} km — FC ${fc(0.60)}–${fc(0.72)} bpm — D+${Math.round(dplus*0.45)}m` },
          { label:"Fin progression", detail:`Derniers 20 min vers ${p(0.70)} /km si énergie disponible` },
        ],
        fcTarget: fc(0.66), ressenti: 3 });
      sessions.push({ day:"Dimanche", type:"fondamentale", km:rawKms[1],
        description:`Trail B2B J2 — jambes fatiguées — allure libre Z1-Z2 — FC < ${fc(0.70)} bpm — D+${Math.round(dplus*0.22)}m`,
        structure:[
          { label:"Échauffement", detail:`10 min à ${p(0.62)} /km` },
          { label:"Corps B2B J2", detail:`${rawKms[1]} km Z1-Z2 — FC < ${fc(0.70)} bpm — D+${Math.round(dplus*0.22)}m` },
          { label:"Retour calme", detail:`5 min marche` },
        ],
        fcTarget: fc(0.68), ressenti: 2 });
      // Séance nocturne ultra (6e séance semaines spécifiques avancées)
      if (dominantObj === "ultra" && hasNightSession) {
        sessions.push({ day:"Vendredi", type:"fondamentale", km:Math.round(effectivePeakKm*0.08),
          description:`🌙 Sortie nocturne — départ 22h — ${Math.round(effectivePeakKm*0.08)}km Z2 frontale — test nutrition solide`,
          structure:[{ label:"Nocturne Z2", detail:`${Math.round(effectivePeakKm*0.08)}km à ${p(0.65)} /km — FC < ${fc(0.70)} bpm` }],
          fcTarget: fc(0.65), ressenti: 2 });
      }
    } else {
      const longKm = rawKms[0];
      const longDesc = isTaper
        ? (taperInfo && taperInfo.raceWNum - weekNum <= 1
            ? `Sortie légère ${longKm}km Z2 — FC < ${fc(0.68)} bpm — 60min max`
            : `Sortie longue ${longKm}km Z2 tranquille — FC ${fc(0.62)}–${fc(0.70)} bpm`)
        : isSpecific
        ? `Sortie longue ${longKm}km — Z2 + finish 20min à allure seuil ${p(0.87)} — FC ${fc(0.82)}–${fc(0.88)} bpm`
        : isBuild
        ? `Sortie longue ${longKm}km — Z2 + finish 20min allure tempo ${p(0.82)} — FC ${fc(0.72)}–${fc(0.80)} bpm`
        : `Sortie longue ${longKm}km — Z2 intégral — ${p(0.68)} — FC ${fc(0.63)}–${fc(0.72)} bpm`;
      sessions.push({ day:"Samedi", type:"sortie_longue", km:longKm,
        description: longDesc,
        structure:[
          { label:"Départ lent", detail:`20 min à ${p(0.62)} /km` },
          { label:"Corps Z2", detail:`${longKm} km — FC ${fc(0.63)}–${fc(0.72)} bpm — ${p(0.68)}` },
          { label:"Progression fin", detail: isSpecific ? `20 min à allure seuil ${p(0.87)}` : isBuild ? `20 min à allure tempo ${p(0.82)}` : `Allure Z2 stable` },
        ],
        fcTarget: fc(0.67), ressenti: isSpecific ? 4 : 3 });

      const qualKm   = rawKms[1];
      const qualType = (isTaper || isRecovery) ? "fondamentale" : isBuild ? "vma" : isSpecific ? "seuil" : "fondamentale";
      const qualDesc = (isTaper || isRecovery)
        ? (isTaper && taperInfo && taperInfo.raceWNum - weekNum <= 1
            ? `Activation — 20min Z2 + 2×1000m à ${p(0.93)} (allure course) — récup 3min`
            : `Footing facile Z2 — ${p(0.68)} — FC < ${fc(0.72)} bpm + 6×100m strides`)
        : isSpecific
        ? `Seuil — 3×10min à ${p(0.87)} — récup 3min trot — FC ${fc(0.82)}–${fc(0.88)} bpm`
        : isBuild
        ? vmaSessionDesc
        : isBase
        ? `Endurance fondamentale — ${p(0.68)} — FC ${fc(0.63)}–${fc(0.72)} bpm`
        : `Footing Z2 — ${p(0.68)}`;
      const qualStructure = qualType === "vma"
        ? [
            { label:"Échauffement 15min + strides", detail:`à ${p(0.65)} /km + 4×20s accélérations` },
            { label:"Corps VMA", detail: vmaSessionDesc },
            { label:"Retour calme", detail:`10 min à ${p(0.60)} /km` },
          ]
        : qualType === "seuil"
        ? [
            { label:"Échauffement 15min", detail:`à ${p(0.65)} /km` },
            { label:"3×10min seuil", detail:`à ${p(0.87)} /km — FC ${fc(0.82)}–${fc(0.88)} bpm — récup 3min trot` },
            { label:"Retour calme", detail:`10 min très lent` },
          ]
        : [
            { label:"Échauffement", detail:`10 min à ${p(0.62)} /km` },
            { label:"Corps fondamental", detail:`${qualKm} km à ${p(0.68)} /km — FC ${fc(0.64)}–${fc(0.72)} bpm` },
            { label:"Retour calme", detail:`5 min très lent` },
          ];
      sessions.push({ day:"Mercredi", type:qualType, km:qualKm,
        description: qualDesc,
        structure: qualStructure,
        fcTarget: qualType === "vma" ? fc(0.90) : qualType === "seuil" ? fc(0.85) : fc(0.68),
        ressenti: qualType === "vma" ? 5 : qualType === "seuil" ? 4 : 2 });

      sessions.push({ day:"Lundi", type:"récup", km:rawKms[2],
        description:`Récupération active — ${p(0.62)} — FC < ${fc(0.58)} bpm — terrain souple`,
        structure:[{ label:"Footing Z1", detail:`${rawKms[2]} km à ${p(0.62)} /km — FC < ${fc(0.58)} bpm` }],
        fcTarget: fc(0.58), ressenti: 1 });

      if (nbS >= 4) {
        const extraType = isSpecific ? "seuil" : "fondamentale";
        const extraIsTrailCotes = isSpecific && isTrailPlan;
        const extraDesc = isSpecific
          ? (isTrailPlan
              ? cotesDesc(8, 0.15)
              : `Seuil progressif — 2×15min à ${p(0.85)} → ${p(0.88)} — récup 3min`)
          : isBuild
          ? `Fondamentale + strides — 50min Z2 + 6×80m accélérations à ${p(0.93)}`
          : `Footing Z2 — ${p(0.68)}`;
        const extraStructure = extraIsTrailCotes
          ? [
              { label:"Échauffement 10min plat", detail:`à ${p(0.65)} /km` },
              cotesStructureBloc(8, 0.15),
              { label:"Retour calme plat", detail:`10 min à ${p(0.62)} /km` },
            ]
          : isSpecific
          ? [
              { label:"Échauffement 15min", detail:`à ${p(0.65)} /km` },
              { label:"2×15min seuil", detail:`à ${p(0.85)}→${p(0.88)} /km — récup 3min` },
              { label:"Retour calme", detail:`10 min très lent` },
            ]
          : [
              { label:"Échauffement", detail:`10 min à ${p(0.62)} /km` },
              { label:"Corps fondamental", detail:`${rawKms[3]} km à ${p(0.68)} /km` },
              { label:"Retour calme", detail:`5 min très lent` },
            ];
        sessions.push({ day:"Jeudi", type: extraIsTrailCotes ? "côtes" : extraType, km:rawKms[3],
          description: extraDesc,
          structure: extraStructure,
          fcTarget: extraIsTrailCotes ? fc(0.88) : isSpecific ? fc(0.87) : fc(0.68),
          ressenti: extraIsTrailCotes ? 4 : isSpecific ? 4 : 2 });
      }

      if (isBase && nbS >= 4) {
        const baseIdx = weekNum;
        const desc = baseIdx <= 1
          ? `Seuil — 2×8min à ${p(0.85)} — récup 3min trot — FC ${fc(0.80)}–${fc(0.86)} bpm`
          : baseIdx <= 2
          ? `Seuil — 3×8min à ${p(0.86)} — récup 3min trot`
          : `Seuil — 2×12min à ${p(0.87)} — récup 3min`;
        const reps = baseIdx <= 1 ? "2×8min" : baseIdx <= 2 ? "3×8min" : "2×12min";
        sessions[sessions.length - 1] = { day:"Jeudi", type:"seuil", km:rawKms[3],
          description: desc,
          structure:[
            { label:"Échauffement 15min", detail:`à ${p(0.65)} /km` },
            { label:`${reps} seuil`, detail:`à ${p(0.86)} /km — FC ${fc(0.82)}–${fc(0.88)} bpm — récup 3min trot` },
            { label:"Retour calme", detail:`10 min très lent` },
          ],
          fcTarget: fc(0.85), ressenti: 4 };
      }

      if (nbS >= 5) {
        const trailCotesDesc5 = cotesDesc(8, 0.12);
        sessions.push({ day:"Mardi", type:isTrailPlan ? "côtes" : "fondamentale", km:rawKms[4],
          description: isTrailPlan ? trailCotesDesc5 : `Footing fondamental — ${p(0.67)} — FC ${fc(0.63)}–${fc(0.70)} bpm`,
          structure: isTrailPlan
            ? [
                { label:"Échauffement 10min plat", detail:`à ${p(0.65)} /km` },
                cotesStructureBloc(8, 0.12),
                { label:"Retour calme plat", detail:`10 min à ${p(0.62)} /km` },
              ]
            : [
                { label:"Échauffement", detail:`10 min à ${p(0.62)} /km` },
                { label:"Corps fondamental", detail:`${rawKms[4]} km à ${p(0.67)} /km — FC ${fc(0.63)}–${fc(0.70)} bpm` },
                { label:"Retour calme", detail:`5 min très lent` },
              ],
          fcTarget: isTrailPlan ? fc(0.88) : fc(0.67),
          ressenti: isTrailPlan ? 4 : 2 });
      }
    }

    const dayOrder = { "Lundi":1,"Mardi":2,"Mercredi":3,"Jeudi":4,"Vendredi":5,"Samedi":6,"Dimanche":7 };
    sessions.sort((a, b) => (dayOrder[a.day] || 9) - (dayOrder[b.day] || 9));

    const volumeKm = sessions.reduce((a, s) => a + (s.km || 0), 0);
    const weekDate = new Date(today);
    weekDate.setDate(today.getDate() + (weekNum - 1) * 7);
    const weekLabel  = weekDate.toLocaleDateString("fr-FR", { day:"2-digit", month:"short" });
    const phaseName  = isRaceWeek ? "Course" : isTaper ? "Taper" : PHASE_NAMES[ph] || "Base";
    const phaseIdx   = isRaceWeek ? 3 : ph;

    weeks.push({ weekNum, phase:phaseName, phaseIdx, volumeKm, dplus, sessions, isRecovery, isRaceWeek, isB2B:!!isB2B, raceWeek:raceMilestones[weekNum]||null, weekLabel });
  }

  return { weeks, totalWeeks, obj: dominantObj, tpl, races: upcoming };
}
